import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { BarChart, Bar, Legend, YAxis } from 'recharts';
import DefaultLegend from '../DefaultLegend';
import { StyledBarWrapper } from './styled';

const STACK_ID = 'single';
const VERTICAL = 'vertical';
const HORIZONTAL = 'horizontal';
const FULL_DOMAIN = [0, 'dataMax'];
const HIGHLIGHT_STROKE = 2;
const HIGHLIGHT_STYLE = { filter: 'brightness(1.2)' };

const R = 10;
const RADIUS_SINGLE = [R, R, R, R];
const RADIUS_TOP = [R, R, 0, 0];
const RADIUS_BOTTOM = [0, 0, R, R];

const getRadius = (index, length, roundBottom) => {
  if (length === 1) return roundBottom ? RADIUS_SINGLE : RADIUS_TOP;
  if (index === 0) return roundBottom ? RADIUS_BOTTOM : undefined;
  if (index === length - 1) return RADIUS_TOP;
  return undefined;
};

/** convert single bar values: [{name: name1, value: value1, color}, {...}, ...] to chart data with single entry:
 * [{name1: value1, name2: value2, ...}]
 * And then all categories share the same stack on a single bar */
const SingleBar = ({
  values,
  width = 300,
  height = 120,
  margin,
  withLegend = true,
  barSize = 40,
  activeIndex,
  onClick,
  highlightIndex,
  roundBottom = true
}) => {
  const data = useMemo(() => {
    const computed = values.reduce((acc, v) => ({ ...acc, [v.name]: v.value }), {});
    return [computed];
  }, [values]);

  const renderLegend = useCallback(
    ({ payload, onClick: onClickLegend, onMouseEnter }) => (
      <DefaultLegend payload={payload} onClick={onClickLegend} onMouseEnter={onMouseEnter} activeIndex={activeIndex} />
    ),
    [activeIndex]
  );
  const clickHandler = useCallback((_, index) => onClick?.(index), [onClick]);

  return (
    <StyledBarWrapper>
      <BarChart data={data} width={width} height={height} margin={margin} layout={HORIZONTAL}>
        <YAxis hide domain={FULL_DOMAIN} />
        {values.map((v, index) => (
          <Bar
            key={v.name}
            dataKey={v.name}
            stackId={STACK_ID}
            fill={v.color}
            cursor={onClick ? 'pointer' : undefined}
            barSize={barSize}
            onClick={() => onClick?.(index)}
            stroke={index === highlightIndex ? v.color : undefined}
            strokeWidth={index === highlightIndex ? HIGHLIGHT_STROKE : 0}
            style={index === highlightIndex ? HIGHLIGHT_STYLE : undefined}
            radius={getRadius(index, values.length, roundBottom)}
          />
        ))}
        {withLegend && (
          <Legend
            layout={VERTICAL}
            verticalAlign="middle"
            align="right"
            onClick={clickHandler}
            content={renderLegend}
          />
        )}
      </BarChart>
    </StyledBarWrapper>
  );
};

SingleBar.propTypes = {
  values: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired
    })
  ).isRequired,
  onClick: PropTypes.func,
  activeIndex: PropTypes.number,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    left: PropTypes.number,
    bottom: PropTypes.number
  }),
  width: PropTypes.number,
  height: PropTypes.number,
  barSize: PropTypes.number,
  withLegend: PropTypes.bool,
  highlightIndex: PropTypes.number,
  roundBottom: PropTypes.bool
};

export default SingleBar;
